// One line of "Recent Pushes": what a send or a removal did, as the server reported it.

import type { CelebrationType, MissedSubtype } from './bannerOptions';

// Mirrors the backend `liveActivity` field returned by /attendance-slots/test-push.
// The APNs client already retried the other host, so this is the FINAL outcome.
type LiveActivityResult = {
  success?: boolean;
  skipped?: boolean;
  status?: number;
  reason?: string;
  host?: string;
  error?: string;
  message?: string;
};

// Mirrors the backend `countdown` field — the Android data-only countdown push.
type CountdownResult = {
  success?: boolean;
  skipped?: boolean;
  error?: string;
  message?: string;
  ticketStatus?: string;
};

// Mirrors the backend `diagnostics` field — "which token did we actually hit?"
type PushDiagnostics = {
  platform?: string | null;
  pushTokenPreview?: string | null;
  pushTokenUpdatedAt?: string | null;
  liveActivity?: {
    tokenPresent: boolean;
    tokenPreview: string | null;
    tokenBytes: number | null;
    tokenSource: string | null;
  };
};

export interface PushLogEntry {
  timestamp: string;
  empCode: string;
  type: string;
  slot?: number;
  /** random_attendance only — which capture mode the test slot was forced into. */
  selfieRequired?: boolean;
  celebrationType?: CelebrationType;
  missedSubtype?: MissedSubtype;
  /** task only — which task was sent or removed. */
  taskType?: string | null;
  success: boolean;
  message: string;
  content?: { title: string; body: string } | null;
  liveActivity?: LiveActivityResult | null;
  countdown?: CountdownResult | null;
  diagnostics?: PushDiagnostics | null;
}

// Render the Live Activity push-to-start outcome as a plain-English verdict, so a
// tester can tell a LIVE (production) token from a SANDBOX/dead one at a glance —
// the same thing check-apns-token.js reports, built into the page.
export function liveActivityVerdict(la?: LiveActivityResult | null): { text: string; ok: boolean } | null {
  if (!la) return null;
  if (la.skipped) return { text: `Live Activity not sent — ${la.message || la.reason || 'skipped'}`, ok: false };
  if (la.success) return { text: `✅ Live Activity card started via ${la.host || 'APNs'} — this token is LIVE`, ok: true };
  const detail = `HTTP ${la.status ?? '-'}${la.reason ? ' ' + la.reason : ''}`;
  const hint =
    la.reason === 'BadEnvironmentKeyInToken'
      ? ' — token is SANDBOX (development build). Needs a production build + a clean delete-and-reinstall.'
      : la.reason === 'BadDeviceToken'
      ? ' — token is dead/stale. Delete the app, reinstall the production build, and log in to re-register.'
      : '';
  return { text: `❌ Live Activity failed: ${detail}${hint}`, ok: false };
}

// Android countdown verdict. A successful send only means Expo/FCM ACCEPTED the
// data push — the card still won't draw if the phone doesn't wake the headless
// task (force-stopped, or OEM battery restriction). Say so, so a green here isn't
// mistaken for "the card definitely appeared."
export function countdownVerdict(cd?: CountdownResult | null): { text: string; ok: boolean } | null {
  if (!cd || cd.skipped) return null;
  if (cd.success) {
    return {
      text: '✅ Android countdown data push accepted by Expo/FCM. If the card still doesn\'t appear on a killed app, the phone isn\'t waking the headless task — check force-stop / battery optimization (Autostart on Xiaomi/Oppo/Vivo/Samsung).',
      ok: true,
    };
  }
  return { text: `❌ Android countdown push failed: ${cd.error || 'unknown error'}`, ok: false };
}
